import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Login } from "@mui/icons-material";
import { API_URL } from "../../util/url";
import fetchWithAuth, { setAuthTokens } from "../../util/fetchWithAuth";
import { useNavigate } from "react-router-dom";
import { canAccess, getRules } from "../../util/rbac";

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const navigate = useNavigate();
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    try {
      const response = await fetch(`${API_URL}/user/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: formData.email,
          password: formData.password,
        }),
      });

      // Read raw response first
      const text = await response.text();
      console.log("Raw login response:", text);

      if (!response.ok) {
        let detail = "";
        try {
          const parsed = JSON.parse(text);
          if (typeof parsed?.detail === "string") {
            detail = parsed.detail;
          } else if (Array.isArray(parsed?.non_field_errors)) {
            detail = parsed.non_field_errors.join(" ");
          } else if (typeof parsed?.message === "string") {
            detail = parsed.message;
          }
        } catch (err) {
          // ignore invalid JSON
        }

        if (response.status === 401) {
          throw new Error("Невірний логін або пароль.");
        }
        if (response.status === 403) {
          throw new Error("Доступ заборонений. Зверніться до адміністратора.");
        }
        if (response.status === 429) {
          throw new Error("Забагато спроб. Спробуйте пізніше.");
        }
        if (response.status >= 500) {
          throw new Error("Помилка сервера. Спробуйте пізніше.");
        }

        throw new Error(detail || `Помилка входу: ${response.status}`);
      }

      // Try to parse JSON
      let data;
      try {
        data = JSON.parse(text);
      } catch (err) {
        throw new Error("Response is not valid JSON: " + text);
      }

      const { access, refresh } = data;
      if (!access || !refresh) {
        throw new Error("Tokens missing in response: " + JSON.stringify(data));
      }

      setAuthTokens(access, refresh);

      // Fetch user profile
      const meResponse = await fetchWithAuth(`${API_URL}/user/me/`);
      const meData = await meResponse.json();

      if (!meResponse.ok) {
        throw new Error(`Failed to fetch user profile: ${meResponse.status}`);
      }

      console.log("User profile data:", meData);

      const rules = getRules(meData);
      const can = (resource) => canAccess(rules, resource, "GET");

      const landing = [
        { resource: "supervisor.dashboard", path: "/supervisor/dashboard/" },
        { resource: "supervisor.scripts", path: "/supervisor/scripts/" },
        { resource: "supervisor.work_schedule", path: "/supervisor/work-schedule/" },
        { resource: "management.dashboard", path: "/management/dashboard" },
        { resource: "management.scripts", path: "/management/scripts/" },
        { resource: "management.work_schedule", path: "/management/work-schedule/" },
        {
          resource: "management.supervisor_work_schedule",
          path: "/management/supervisor-work-schedule/",
        },
        { resource: "operator.dashboard", path: "/operator/dashboard" },
        { resource: "operator.scripts", path: "/operator/scripts/" },
        { resource: "operator.work_schedule", path: "/operator/work-schedule" },
      ];

      const target = landing.find((item) => can(item.resource));
      if (target) {
        navigate(target.path);
        return;
      }

      throw new Error("Немає прав для доступу до сторінок.");
    } catch (err) {
      console.error("Login error:", err.message);
      setErrorMessage(err.message || "Помилка входу.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#0d1117",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 483, px: 3 }}>
        <Typography
          variant="h5"
          sx={{
            color: "#fff",
            fontWeight: "bold",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          Ласкаво просимо до
          <img src="/icons/logo.svg" alt="logo" style={{ height: "24px" }} />
        </Typography>

        <Typography
          sx={{
            mt: 1,
            color: "#9ca3af",
            textAlign: "left",
            fontSize: "0.7rem",
          }}
        >
          Введіть дані, які ви отримали від нас на пошту.
        </Typography>

        <Typography sx={{ mt: 3, mb: 1, color: "#fff", fontSize: "0.85rem" }}>
          Логін
        </Typography>
        <TextField
          fullWidth
          name="email"
          placeholder="testmail@test.com"
          value={formData.email}
          onChange={handleChange}
          variant="outlined"
          InputProps={{
            style: { color: "#fff" },
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              bgcolor: "transparent",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "#2d3748",
              },
              "&:hover fieldset": {
                borderColor: "#4f46e5",
              },
            },
            input: { color: "#fff" },
          }}
        />

        <Typography sx={{ mb: 1, color: "#fff", fontSize: "0.85rem" }}>
          Пароль
        </Typography>
        <TextField
          fullWidth
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder="Ваш пароль"
          value={formData.password}
          onChange={handleChange}
          variant="outlined"
          InputProps={{
            style: { color: "#fff" },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: "#9ca3af" }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              bgcolor: "transparent",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "#2d3748",
              },
              "&:hover fieldset": {
                borderColor: "#4f46e5",
              },
            },
          }}
        />

        <Button
          fullWidth
          variant="contained"
          startIcon={<Login />}
          sx={{
            bgcolor: "#696cff",
            color: "#fff",
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: "bold",
            py: 1.2,
            borderRadius: "8px",
            "&:hover": {
              bgcolor: "#5a5ce5",
            },
          }}
          onClick={handleSubmit}
        >
          Вхід
        </Button>
        {errorMessage && (
          <Typography sx={{ mt: 2, color: "#f87171", fontSize: "0.85rem" }}>
            {errorMessage}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
